import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmailPreviewFrame } from "./EmailPreviewFrame";

/** Operator review before send: recipients, From/Reply-To, subject, rendered email, attachments, warnings. */
export const OutboxPreviewDialog = ({ outboxId, onClose, onSent }) => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!outboxId) return;
    setP(null);
    api.get(`/comms/outbox/${outboxId}/preview`).then(({ data }) => setP(data)).catch((e) => { toast.error(e.response?.data?.detail || "Preview failed"); onClose(); });
  }, [outboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!window.confirm(`Send this email to ${p.to} now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/comms/outbox/${outboxId}/send`, {});
      data.ok ? toast.success(`Accepted by provider (${data.outcome})`) : toast.error(data.error || "Not sent");
      onSent?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  const canSend = p && !p.blocked && ["awaiting_review", "failed", "queued"].includes(p.status);
  return (
    <Dialog open={!!outboxId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="outbox-preview-dialog">
        <DialogHeader><DialogTitle className="text-base">Review before sending</DialogTitle></DialogHeader>
        {!p ? <div className="text-sm text-slate-400 p-6">Rendering…</div> : (
          <div className="space-y-3 text-[12px]">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 border border-slate-200 rounded-md p-3 bg-slate-50/60">
              <div><span className="text-slate-500">To:</span> <span className="font-medium" data-testid="outbox-preview-to">{p.to || "— missing —"}</span></div>
              <div><span className="text-slate-500">From:</span> <span className="font-medium" data-testid="outbox-preview-from">{p.from}</span></div>
              <div className="sm:col-span-2"><span className="text-slate-500">Subject:</span> <span className="font-medium" data-testid="outbox-preview-subject">{p.subject}</span></div>
              <div><span className="text-slate-500">Reply-To:</span> <span className="font-medium" data-testid="outbox-preview-reply-to">{p.reply_to}</span></div>
              <div><span className="text-slate-500">Attachments:</span> {p.attachments?.length ? p.attachments.map((a) => a.filename).join(", ") : "none"}</div>
              <div className="sm:col-span-2 text-slate-400">Template {p.template_id} v{p.template_version} · status {p.status}</div>
            </div>
            {p.warnings?.length > 0 && (
              <div className={`border rounded-md p-3 ${p.blocked ? "border-rose-300 bg-rose-50 text-rose-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="outbox-preview-warnings">
                <div className="font-medium mb-1">{p.blocked ? "Sending is blocked until these are resolved:" : "Please check before sending:"}</div>
                <ul className="list-disc pl-4 space-y-0.5">{p.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
              </div>
            )}
            <EmailPreviewFrame html={p.html} text={p.text} testid="outbox-preview" />
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={onClose} data-testid="outbox-preview-close">Close</Button>
              {canSend && <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} onClick={send} data-testid="outbox-preview-send">Approve &amp; send</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
